import { SETTINGS_SECTIONS } from '../../app/terminology/settings';
// features/vehicles/VehiclePage.tsx
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '../../app/stores/store';
import agent from '../../app/api/agent';
import type { Vehicle, VehicleCreate, VehicleEdit, VehicleGroupEditResult } from '../../app/models/Vehicle';
import type { FitmentCatalogSettings } from '../../app/models/FitmentCatalog';
import { choiceValues, fieldChecked, fieldValue, useModalOpen, usePolarisEvents, useRowSelection } from '../../app/components/polaris';
import Combobox from '../../app/components/Combobox';
import MappingFilters, { type AppliedMappingFilter, type MappingFilter, type MappingSortOption } from '../products/MappingFilters';
import {
    getFitmentFieldEmptyOptionLabel,
    getFitmentFieldValue,
    getLegacyFieldStateKey,
    getLegacyVehicleFieldValue,
    getVisibleFitmentFields,
    hasAnyFitmentFieldValue,
    isFitmentFieldDisabled,
    normalizeFitmentFieldValues,
    normalizeFitmentKey,
    updateFitmentFieldValue,
    type FitmentFieldValues,
} from '../../app/utils/fitmentFields';
import {
    FITMENT_CATALOG_LABEL_CHANGED,
    getCachedFitmentCatalogLabel,
    getStoredFitmentCatalogLabel,
    setCachedFitmentCatalogLabel,
} from '../../app/utils/fitmentCatalogLabel';
import { vehicleEditorValues, vehicleEditorPayload, validateVehicleEditor, type VehicleEditorErrors } from './vehicleEditor';
import { catalogColumns, formatVehicleYears, VehicleCatalogRows } from './VehicleCatalogRows';

type SelectOption = { label: string; value: string };
type FitmentField = ReturnType<typeof getVisibleFitmentFields>[number];

const DEBOUNCE_MS = 500;

const vehicleFieldSortValue = 'fitment-fields asc';

const yearRangeError = (from: string, to: string) => {
    const valid = (value: string) => !value || (/^-?\d+$/.test(value)
        && Number(value) >= -2147483648 && Number(value) <= 2147483647);
    if (!valid(from) || !valid(to)) return 'Enter a whole-number year.';
    return from && to && Number(from) > Number(to)
        ? 'From year must be less than or equal to To year.' : '';
};

const isStandardFitmentField = (field: FitmentField, key: string) =>
    (field.key || '').trim().toLowerCase() === key;

const orderFitmentFieldsWithYearLast = (fields: FitmentField[]) => {
    const yearField = fields.find((field) => isStandardFitmentField(field, 'year'));
    return yearField
        ? [...fields.filter((field) => field !== yearField), yearField]
        : fields;
};

/**
 * Typeahead for one fitment field. Typing only narrows the suggestions; the
 * list is filtered when a value is picked or cleared, as before.
 */
const FitmentFilterCombobox = ({
    label,
    value,
    options,
    disabled,
    loading,
    placeholder,
    onChange,
}: {
    label: string;
    value: string;
    options: SelectOption[];
    disabled?: boolean;
    loading?: boolean;
    placeholder: string;
    onChange: (value: string) => void;
}) => {
    const [query, setQuery] = useState(value);

    useEffect(() => {
        setQuery(value);
    }, [value]);

    return (
        <Combobox
            label={label}
            value={query}
            options={options}
            disabled={disabled}
            loading={loading}
            placeholder={placeholder}
            emptyMessage="No matching values."
            onChange={(next, immediate) => {
                setQuery(next);
                if (immediate) onChange(next);
            }}
        />
    );
};

declare global {
    interface Window {
        shopify?: {
            idToken?: () => Promise<string>;
            toast?: { show?: (msg: string | { message: string; duration?: number }) => void };
        };
    }
}

const showToast = (msg: string) => window.shopify?.toast?.show?.(msg);

const vehicleRequestKey = (args: unknown[]) => JSON.stringify([
    ...args.slice(0, 8),
    Object.entries((args[8] ?? {}) as Record<string, string>)
        .filter(([, value]) => !!value).sort(([a], [b]) => a.localeCompare(b)),
    args[9],
    args[10] || '', args[11] || '',
]);

const VehiclesPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const requestedYear = new URLSearchParams(location.search).get('year')?.trim() || '';
    const { vehicleStore } = useStore();
    const { vehicles, loadVehicles: fetchVehicles, page, hasNext, totalCount, loadingInitial } = vehicleStore;
    const [yearView, setYearView] = useState<'ranges' | 'individual'>('ranges');
    const yearViewRef = useRef(yearView);
    // Field suggestions are loaded on first use of the vehicle fields.
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filterYearFrom, setFilterYearFrom] = useState('');
    const [filterYearTo, setFilterYearTo] = useState('');
    const filterYearRangeRef = useRef({ from: '', to: '' });
    const rangeError = yearRangeError(filterYearFrom, filterYearTo);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const lastVehicleRequest = useRef('');
    const loadVehicles = useCallback((...args: Parameters<typeof fetchVehicles>) => {
        const { from, to } = filterYearRangeRef.current;
        if (yearRangeError(from, to)) return;
        args[9] = yearViewRef.current;
        args[10] = from;
        args[11] = to;
        lastVehicleRequest.current = vehicleRequestKey(args);
        return fetchVehicles(...args);
    }, [fetchVehicles]);
    const [fitmentCatalog, setFitmentCatalog] = useState<FitmentCatalogSettings | null>(null);
    const [vehicleCatalogLabel, setVehicleCatalogLabel] = useState(() => getStoredFitmentCatalogLabel() ?? "");

    const visibleFitmentFields = useMemo(
        () => getVisibleFitmentFields(fitmentCatalog),
        [fitmentCatalog],
    );
    const vehicleListFitmentFields = useMemo(
        () => orderFitmentFieldsWithYearLast(visibleFitmentFields),
        [visibleFitmentFields],
    );
    const legacyVisibleFields = useMemo(
        () => visibleFitmentFields.filter((field) => getLegacyFieldStateKey(field)),
        [visibleFitmentFields],
    );
    const fieldEnabled = useMemo(() => {
        const enabled = new Set(legacyVisibleFields.map((field) => getLegacyFieldStateKey(field)).filter(Boolean));
        return {
            year: enabled.has('year'),
            make: enabled.has('make'),
            model: enabled.has('model'),
            submodel: enabled.has('submodel'),
        };
    }, [legacyVisibleFields]);

    // ---------- Modal open/close ----------
    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingIsCustom, setEditingIsCustom] = useState(false);
    const [persistFitments, setPersistFitments] = useState(true);

    // Set while editing every year of a group; the year itself is then read-only.
    const [editingGroup, setEditingGroup] = useState<Vehicle[] | null>(null);
    const [mergeReview, setMergeReview] = useState<VehicleGroupEditResult | null>(null);

    const openAdd = useCallback(() => {
        setEditingId(null);
        setEditingIsCustom(false);
        setEditingGroup(null);
        setMergeReview(null);
        setPersistFitments(true);
        setModalOpen(true);
    }, []);

    const [formValues, setFormValues] = useState<FitmentFieldValues>({});
    const [yearTo, setYearTo] = useState('');
    const [errors, setErrors] = useState<VehicleEditorErrors>({});
    const [submitError, setSubmitError] = useState<string | undefined>();
    const [submitting, setSubmitting] = useState(false);
    const [lastAdded, setLastAdded] = useState<VehicleCreate | null>(null);
    const [lastAddedExists, setLastAddedExists] = useState(false);

    const onEditClick = useCallback((v: Vehicle) => {
        setEditingId(v.id);
        setEditingIsCustom(!!v.isCustom);
        setEditingGroup(null);
        setMergeReview(null);
        const values = vehicleEditorValues(visibleFitmentFields, v);
        setFormValues(values);
        setYearTo('');
        setPersistFitments(true);
        setErrors({});
        setSubmitError(undefined);
        setModalOpen(true);
    }, [visibleFitmentFields]);

    const onEditGroupClick = useCallback((records: Vehicle[]) => {
        setEditingId(null);
        setEditingIsCustom(true);
        setEditingGroup(records);
        setMergeReview(null);
        setFormValues(vehicleEditorValues(visibleFitmentFields, records[0]));
        setYearTo('');
        setErrors({});
        setSubmitError(undefined);
        setModalOpen(true);
    }, [visibleFitmentFields]);

    const resetForm = useCallback(() => {
        setFormValues({});
        setYearTo('');
        setErrors({});
        setSubmitError(undefined);
    }, []);

    const handleClose = useCallback(() => {
        resetForm();
        setLastAdded(null);
        setLastAddedExists(false);
        setEditingId(null);
        setEditingIsCustom(false);
        setEditingGroup(null);
        setMergeReview(null);
        setModalOpen(false);
    }, [resetForm]);

    const dismissLastAdded = useCallback(() => {
        setLastAdded(null);
        setLastAddedExists(false);
    }, []);

    const onCopyLast = useCallback(() => {
        if (!lastAdded) return;
        setFormValues({ ...lastAdded.fieldValues });
        setYearTo(lastAdded.yearTo && lastAdded.yearTo !== lastAdded.yearFrom ? String(lastAdded.yearTo) : '');
        dismissLastAdded();
    }, [dismissLastAdded, lastAdded]);

    const lastAddedLabel = useMemo(() => {
        if (!lastAdded) return '';
        return visibleFitmentFields.map(field => {
            const key = normalizeFitmentKey(field);
            if (key === 'year' && lastAdded.yearTo !== lastAdded.yearFrom)
                return `${lastAdded.yearFrom}-${lastAdded.yearTo}`;
            return lastAdded.fieldValues?.[key] || '';
        }).filter(Boolean).join(', ');
    }, [lastAdded, visibleFitmentFields]);

    const onSaveGroup = async () => {
        setSubmitError(undefined);
        const fields = visibleFitmentFields.filter(field => normalizeFitmentKey(field) !== 'year');
        const nextErrors = validateVehicleEditor(fields, formValues, '');
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0 || !editingGroup) return;

        setSubmitting(true);
        try {
            const result = await vehicleStore.updateGroup({
                ids: editingGroup.map(record => record.id),
                fieldValues: vehicleEditorPayload(fields, formValues, '').fieldValues!,
                confirmMerge: !!mergeReview,
            });
            if (!result.saved) {
                setMergeReview(result);
                return;
            }
            await reloadVehicles(1);
            showToast(`${editingGroup.length} years saved`);
            handleClose();
        } catch (err: any) {
            setSubmitError(err?.response?.data?.message ?? err?.message ?? 'Failed to save vehicle');
        } finally {
            setSubmitting(false);
        }
    };

    const onSave = async () => {
        setSubmitError(undefined);
        const nextErrors = validateVehicleEditor(visibleFitmentFields, formValues, editingIsCustom ? '' : yearTo);
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return;

        setSubmitting(true);
        const basePayload = vehicleEditorPayload(visibleFitmentFields, formValues, editingIsCustom ? '' : yearTo);

        try {
            if (editingId) {
                const payload: VehicleEdit = { ...basePayload, isCustom: editingIsCustom, persistFitments };
                await vehicleStore.update(editingId, payload);
                await reloadVehicles(1);
                showToast('Vehicle saved');
            } else {
                await vehicleStore.create(basePayload);
                await reloadVehicles(1);
                showToast('Vehicle added');
                setLastAdded(basePayload);
                setLastAddedExists(false);
                resetForm();
            }
        } catch (err: any) {
            if (!editingId && err?.response?.status === 409) {
                await reloadVehicles(1);
                showToast('Vehicle already exists');
                setLastAdded(basePayload);
                setLastAddedExists(true);
                resetForm();
            } else {
                setSubmitError(err?.response?.data?.message ?? err?.message ?? (editingId ? 'Failed to save vehicle' : 'Failed to create vehicle'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    // ---------- Existing list logic ----------
    const [queryValue, setQueryValue] = useState('');
    const [selectedSource, setSelectedSource] = useState<string[]>([]);
    const [selectedAssigned, setSelectedAssigned] = useState<string[]>([]);
    const [sortSelected, setSortSelected] = useState<string[]>([vehicleFieldSortValue]);
    const [selectedYear, setSelectedYear] = useState(requestedYear);
    const [selectedMake, setSelectedMake] = useState('');
    const [selectedModel, setSelectedModel] = useState('');
    const [selectedAdditionalFields, setSelectedAdditionalFields] = useState<FitmentFieldValues>({});
    const [fieldOptions, setFieldOptions] = useState<Record<string, SelectOption[]>>({});
    const [loadingFieldOptions, setLoadingFieldOptions] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const skipNextDebouncedLoadRef = useRef(false);
    const didInitialLoadRef = useRef(false);
    const selectedFitmentValues = useMemo(
        // Keep legacy state variables for existing sort/filter calls, but also
        // expose the full field map so custom selector fields can participate.
        () => normalizeFitmentFieldValues(vehicleListFitmentFields, {
            ...selectedAdditionalFields,
            year: selectedYear,
            make: selectedMake,
            model: selectedModel,
        }),
        [selectedAdditionalFields, selectedMake, selectedModel, selectedYear, vehicleListFitmentFields],
    );
    const storedFitmentValues = useMemo(
        () => normalizeFitmentFieldValues(vehicleListFitmentFields, {
            ...vehicleStore.fieldValues,
            year: vehicleStore.year,
            make: vehicleStore.make,
            model: vehicleStore.model,
        }),
        [vehicleListFitmentFields, vehicleStore.fieldValues, vehicleStore.year, vehicleStore.make, vehicleStore.model],
    );
    const hasStoredVehicleDropdownFilter = hasAnyFitmentFieldValue(vehicleListFitmentFields, storedFitmentValues);
    const hasVehicleDropdownFilter = hasAnyFitmentFieldValue(vehicleListFitmentFields, selectedFitmentValues);
    const hasActiveVehicleFilters =
        !!filterYearFrom || !!filterYearTo
        || queryValue.trim().length > 0
        || selectedSource.some(Boolean)
        || selectedAssigned.some(Boolean)
        || hasVehicleDropdownFilter
        || vehicleStore.searchTerm.trim().length > 0
        || !!vehicleStore.source
        || !!vehicleStore.assigned
        || hasStoredVehicleDropdownFilter;

    // Reloads with the current search, filters and sort.
    const reloadVehicles = (targetPage: number) => loadVehicles(
        targetPage,
        queryValue,
        selectedSource[0] || '',
        sortSelected[0],
        selectedYear,
        selectedMake,
        selectedModel,
        selectedAssigned[0] || '',
        selectedFitmentValues,
    );

    const applySelectedFitmentValues = useCallback((nextValues: FitmentFieldValues) => {
        const normalized = normalizeFitmentFieldValues(vehicleListFitmentFields, nextValues);
        // Split the generic map back into legacy fields plus "everything else".
        // This keeps older vehicle APIs working while the UI can render 4-8
        // merchant-defined fields.
        setSelectedYear(normalized.year || '');
        setSelectedMake(normalized.make || '');
        setSelectedModel(normalized.model || '');
        setSelectedAdditionalFields(
            Object.entries(normalized)
                .filter(([key]) => key !== 'year' && key !== 'make' && key !== 'model')
                .reduce<FitmentFieldValues>((accumulator, [key, value]) => {
                    accumulator[key] = value;
                    return accumulator;
                }, {})
        );
    }, [vehicleListFitmentFields]);

    useEffect(() => {
        // Dashboard review links must start from a clean scope, including when
        // the store still holds filters from an earlier visit to this page.
        setSelectedYear(requestedYear);
        filterYearRangeRef.current = { from: '', to: '' };
        setFilterYearFrom('');
        setFilterYearTo('');
        setQueryValue('');
        setSelectedSource([]);
        setSelectedAssigned([]);
        setSelectedMake('');
        setSelectedModel('');
        setSelectedAdditionalFields({});
        setSortSelected([vehicleFieldSortValue]);
        loadVehicles(1, '', '', vehicleFieldSortValue, requestedYear, '', '', '',
            requestedYear ? { year: requestedYear } : {});
        didInitialLoadRef.current = true;
    }, [loadVehicles, requestedYear]);

    useEffect(() => {
        let cancelled = false;

        const loadFitmentCatalog = async () => {
            try {
                const catalog = await agent.Settings.fitmentCatalog();
                if (!cancelled) {
                    setFitmentCatalog(catalog);
                    setVehicleCatalogLabel(setCachedFitmentCatalogLabel(catalog.name));
                }
            } catch {
                // The field helper falls back to the default YMMS shape when
                // settings cannot be loaded, keeping the page usable.
                if (!cancelled) {
                    setFitmentCatalog(null);
                    setVehicleCatalogLabel(getCachedFitmentCatalogLabel());
                }
            }
        };

        const handleLabelChange = (event: Event) => {
            const nextLabel = (event as CustomEvent<string>).detail;
            if (nextLabel) setVehicleCatalogLabel(nextLabel);
        };

        window.addEventListener(FITMENT_CATALOG_LABEL_CHANGED, handleLabelChange);
        loadFitmentCatalog();
        return () => {
            cancelled = true;
            window.removeEventListener(FITMENT_CATALOG_LABEL_CHANGED, handleLabelChange);
        };
    }, []);

    useEffect(() => {
        if (!didInitialLoadRef.current) return;
        if (skipNextDebouncedLoadRef.current) {
            skipNextDebouncedLoadRef.current = false;
            return;
        }

        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(() => {
            const args: Parameters<typeof fetchVehicles> = [
                1,
                queryValue,
                selectedSource[0] || '',
                sortSelected[0],
                selectedYear,
                selectedMake,
                selectedModel,
                selectedAssigned[0] || '',
                selectedFitmentValues,
                yearViewRef.current,
                filterYearFrom,
                filterYearTo,
            ];
            // Settings and state initialization can rerender without changing
            // the query. Do not restart the same expensive catalog request.
            if (vehicleRequestKey(args) !== lastVehicleRequest.current) loadVehicles(...args);
        }, DEBOUNCE_MS);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [queryValue, selectedSource, selectedAssigned, sortSelected, selectedYear, selectedMake, selectedModel, selectedFitmentValues, filterYearFrom, filterYearTo, loadVehicles]);

    useEffect(() => {
        // If settings disable a legacy field, clear its state so hidden values
        // do not continue filtering the vehicle list.
        if (!fieldEnabled.year) {
            setSelectedYear('');
            setFilterYearFrom('');
            setFilterYearTo('');
            filterYearRangeRef.current = { from: '', to: '' };
        }
        if (!fieldEnabled.make) setSelectedMake('');
        if (!fieldEnabled.model) setSelectedModel('');
    }, [fieldEnabled.make, fieldEnabled.model, fieldEnabled.year]);

    useEffect(() => {
        let cancelled = false;

        const loadOptions = async () => {
            if (!filtersOpen) return;
            if (vehicleListFitmentFields.length === 0) {
                setFieldOptions({});
                return;
            }

            setLoadingFieldOptions(true);
            try {
                // The admin selector uses every enabled field. Required/search rules
                // control validation and disabled states, not whether the field appears.
                const result = await vehicleStore.filterOptions(
                    selectedFitmentValues.year || '',
                    selectedFitmentValues.make || '',
                    selectedSource[0] || '',
                    selectedFitmentValues,
                    vehicleListFitmentFields.map((field) => normalizeFitmentKey(field)),
                );

                if (cancelled) return;

                const nextOptions = vehicleListFitmentFields.reduce<Record<string, SelectOption[]>>((accumulator, field) => {
                    const key = normalizeFitmentKey(field);
                    const values = result.optionsByField?.[key] ?? [];
                    accumulator[key] = [
                        { label: getFitmentFieldEmptyOptionLabel(field, 'all'), value: '' },
                        ...values.map((value) => ({ label: value, value })),
                    ];
                    return accumulator;
                }, {});

                setFieldOptions(nextOptions);
            } catch {
                if (!cancelled) setFieldOptions({});
            } finally {
                if (!cancelled) setLoadingFieldOptions(false);
            }
        };

        loadOptions();
        return () => {
            cancelled = true;
        };
    }, [filtersOpen, fitmentCatalog, selectedFitmentValues, selectedSource, vehicleStore, vehicleListFitmentFields]);

    // Vehicle dropdown filters are applied server-side so the list remains paged.
    // Do not pull all pages into the client; the API should return one page of
    // filtered results at a time.

    const changeYearRange = (from: string, to: string) => {
        filterYearRangeRef.current = { from, to };
        setFilterYearFrom(from);
        setFilterYearTo(to);
        setSelectedYear('');
        if ((from || to) && !yearRangeError(from, to)) {
            yearViewRef.current = 'individual';
            setYearView('individual');
        }
    };

    const sortOptions: MappingSortOption[] = [
        { label: SETTINGS_SECTIONS.selectorFields, value: vehicleFieldSortValue, directionLabel: 'A–Z' },
        { label: SETTINGS_SECTIONS.selectorFields, value: 'fitment-fields desc', directionLabel: 'Z–A' },
        ...(fieldEnabled.year ? [
            { label: 'Year', value: 'year desc', directionLabel: 'Newest first' },
            { label: 'Year', value: 'year asc', directionLabel: 'Oldest first' },
        ] : []),
        { label: 'Assigned products', value: 'assigned asc', directionLabel: 'Fewest' },
        { label: 'Assigned products', value: 'assigned desc', directionLabel: 'Most' },
    ];

    const clearVehicleFields = useCallback(() => {
        skipNextDebouncedLoadRef.current = true;
        setSelectedYear('');
        setSelectedMake('');
        setSelectedModel('');
        setSelectedAdditionalFields({});
        filterYearRangeRef.current = { from: '', to: '' };
        setFilterYearFrom('');
        setFilterYearTo('');
        loadVehicles(1, queryValue, selectedSource[0] || '', sortSelected[0], '', '', '', selectedAssigned[0] || '', {});
    }, [loadVehicles, queryValue, selectedAssigned, selectedSource, sortSelected]);

    const clearListFilters = useCallback(() => {
        setQueryValue('');
        setSelectedSource([]);
        setSelectedAssigned([]);
    }, []);

    const onClearAll = useCallback(() => {
        skipNextDebouncedLoadRef.current = true;
        setQueryValue('');
        setSelectedSource([]);
        setSelectedAssigned([]);
        setSortSelected([vehicleFieldSortValue]);
        setSelectedYear('');
        setSelectedMake('');
        setSelectedModel('');
        setSelectedAdditionalFields({});

        filterYearRangeRef.current = { from: '', to: '' };
        setFilterYearFrom('');
        setFilterYearTo('');

        loadVehicles(1, '', '', vehicleFieldSortValue, '', '', '', '', {});
    }, [loadVehicles]);

    const hasPrevious = page > 1;

    // Never allow selection of stale rows while new year bounds are pending,
    // invalid, or failed. Bulk deletion must match the years being reviewed.
    const rangeResultsPending = !!rangeError
        || filterYearFrom !== (vehicleStore.yearFrom || '')
        || filterYearTo !== (vehicleStore.yearTo || '');
    const filteredVehicles = useMemo(() => {
        return !rangeResultsPending && !loadingInitial && Array.isArray(vehicles) ? vehicles : [];
    }, [vehicles, rangeResultsPending, loadingInitial]);

    const rows: Vehicle[] = filteredVehicles;
    const showCreateVehicleEmptyState =
        didInitialLoadRef.current
        && !loadingInitial
        && !vehicleStore.loadError
        && rows.length === 0
        && totalCount === 0
        && !hasActiveVehicleFilters;
    const selection = useRowSelection(rows.map(v => String(v.id)));
    const { selected: selectedResources, allMatching: allResourcesSelected, clear: clearSelection } = selection;
    const selectionScope = JSON.stringify([page, yearView, queryValue, selectedSource, selectedAssigned, sortSelected, selectedFitmentValues, filterYearFrom, filterYearTo]);
    useEffect(() => {
        clearSelection();
        setExpandedGroups(new Set());
    }, [clearSelection, selectionScope]);
    const displayedColumns = catalogColumns(vehicleListFitmentFields);
    const combinedIdentity = displayedColumns.length < vehicleListFitmentFields.length;
    const openAssignedProducts = useCallback((vehicle: Vehicle) => {
        const params = new URLSearchParams();
        const fieldValues = vehicleListFitmentFields.reduce<FitmentFieldValues>((accumulator, field) => {
            const key = normalizeFitmentKey(field);
            const value = getLegacyVehicleFieldValue(vehicle, field);
            if (key && value && value !== '-') {
                accumulator[key] = value;
            }
            return accumulator;
        }, {});
        const year = fieldValues.year || '';
        const make = fieldValues.make || '';
        const model = fieldValues.model || '';

        if (year) params.set('year', year);
        if (make) params.set('make', make);
        if (model) params.set('model', model);
        if (Object.keys(fieldValues).length > 0) {
            params.set('fieldValues', JSON.stringify(fieldValues));
        }
        params.set('mappedOnly', 'true');

        navigate(`/products?${params.toString()}`);
    }, [navigate, vehicleListFitmentFields]);
    // ---------- BULK ACTIONS ----------
    const selectedCount = allResourcesSelected ? totalCount : selectedResources.length;

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteInfo, setDeleteInfo] = useState<{ hasCustom: boolean; hasStandard: boolean; customCount: number }>({
        hasCustom: false,
        hasStandard: false,
        customCount: 0,
    });

    const onBulkDeleteClick = useCallback(() => {
        if (rangeResultsPending || loadingInitial) return;
        const count = allResourcesSelected ? totalCount : selectedResources.length;
        if (count < 1) return;

        if (allResourcesSelected) {
            const hasStandard = selectedSource[0] !== 'Custom';
            const hasCustom = selectedSource[0] !== 'System';
            setDeleteInfo({ hasStandard, hasCustom, customCount: hasCustom ? count : 0 });
        } else {
            const selectedVehicles = rows.filter((v) => selectedResources.includes(String(v.id)));
            const customVehicles = selectedVehicles.filter((v) => v.isCustom);

            setDeleteInfo({
                hasStandard: selectedVehicles.length > customVehicles.length,
                hasCustom: customVehicles.length > 0,
                customCount: customVehicles.length,
            });
        }

        setDeleteOpen(true);
    }, [allResourcesSelected, totalCount, selectedResources, rows, selectedSource, rangeResultsPending, loadingInitial]);

    const confirmDelete = useCallback(async () => {
        if (rangeResultsPending || loadingInitial) return;
        setDeleting(true);

        try {
            if (typeof (vehicleStore as any).bulkDelete === 'function') {
                if (allResourcesSelected) {
                    await (vehicleStore as any).bulkDelete({
                        ids: null,
                        scope: {
                            query: queryValue,
                            source: 'Custom',
                            sort: sortSelected[0],
                            year: selectedYear,
                            yearFrom: filterYearFrom ? Number(filterYearFrom) : undefined,
                            yearTo: filterYearTo ? Number(filterYearTo) : undefined,
                            make: selectedMake,
                            model: selectedModel,
                            fieldValues: selectedFitmentValues,
                            assigned: selectedAssigned[0] || '',
                        },
                    });
                } else {
                    const ids = rows
                        .filter((v) => selectedResources.includes(String(v.id)) && v.isCustom)
                        .map((v) => Number(v.id));

                    await (vehicleStore as any).bulkDelete({ ids });
                }
            } else {
                const ids = allResourcesSelected
                    ? []
                    : rows
                        .filter((v) => selectedResources.includes(String(v.id)) && v.isCustom)
                        .map((v) => Number(v.id));

                await Promise.all(ids.map((id) => (vehicleStore as any).delete?.(id)));
            }

            showToast('Vehicles deleted');
            setDeleteOpen(false);
            clearSelection();

            await loadVehicles(
                1,
                queryValue,
                selectedSource[0] || '',
                sortSelected[0],
                selectedYear,
                selectedMake,
                selectedModel,
                selectedAssigned[0] || '',
                selectedFitmentValues,
            );
        } catch (e: any) {
            showToast(e?.message ?? 'Failed to delete');
        } finally {
            setDeleting(false);
        }
    }, [
        allResourcesSelected,
        selectedResources,
        rows,
        vehicleStore,
        clearSelection,
        loadVehicles,
        queryValue,
        selectedSource,
        selectedAssigned,
        sortSelected,
        selectedYear,
        selectedMake,
        selectedModel,
        selectedFitmentValues,
        filterYearFrom,
        filterYearTo,
        rangeResultsPending,
        loadingInitial,
    ]);

    const tableEvents = usePolarisEvents({
        nextpage: () => { if (hasNext) reloadVehicles(page + 1); },
        previouspage: () => { if (hasPrevious) reloadVehicles(page - 1); },
    });
    const lastAddedEvents = usePolarisEvents({ dismiss: dismissLastAdded });
    const editorModalRef = useModalOpen(modalOpen, () => { if (!submitting) handleClose(); });
    const deleteModalRef = useModalOpen(deleteOpen, () => { if (!deleting) setDeleteOpen(false); });

    const filters: MappingFilter[] = [
        {
            key: 'source',
            label: 'Data source',
            filter: (
                <s-choice-list
                    label="Data source"
                    labelAccessibilityVisibility="exclusive"
                    values={selectedSource}
                    onChange={(event) => setSelectedSource(choiceValues(event))}
                >
                    <s-choice value="System">System</s-choice>
                    <s-choice value="Custom">User-added</s-choice>
                    <s-choice value="ATD">ATD</s-choice>
                    <s-choice value="Turn14">Turn14</s-choice>
                </s-choice-list>
            ),
        },
        {
            key: 'assigned',
            label: 'Assigned products',
            filter: (
                <s-choice-list
                    label="Assigned products"
                    labelAccessibilityVisibility="exclusive"
                    values={selectedAssigned}
                    onChange={(event) => setSelectedAssigned(choiceValues(event))}
                >
                    <s-choice value="with">With assigned products</s-choice>
                    <s-choice value="without">Without assigned products</s-choice>
                </s-choice-list>
            ),
        },
    ];

    const appliedFilters: AppliedMappingFilter[] = [];
    if (selectedSource.length > 0) {
        appliedFilters.push({
            key: 'source',
            label: `Source: ${selectedSource[0] === 'Custom' ? 'User-added' : selectedSource[0]}`,
            onRemove: () => setSelectedSource([]),
        });
    }
    if (selectedAssigned.length > 0) {
        appliedFilters.push({
            key: 'assigned',
            label: selectedAssigned[0] === 'with' ? 'With assigned products' : 'Without assigned products',
            onRemove: () => setSelectedAssigned([]),
        });
    }

    // One row of vehicle fields on wide screens (From and To year side by
    // side), two columns on narrow ones, as on the product list.
    const nonYearFields = vehicleListFitmentFields.filter(field => !isStandardFitmentField(field, 'year'));
    const yearOptions = fieldOptions.year ?? [];
    const vehicleFieldCount = nonYearFields.length + (fieldEnabled.year ? 2 : 0);
    const vehicleGridColumns = `@container (inline-size > ${Math.max(560, vehicleFieldCount * 150)}px) ${'1fr '.repeat(vehicleFieldCount)}auto, 1fr 1fr`;
    const hasVehicleSelection = hasVehicleDropdownFilter || !!filterYearFrom || !!filterYearTo;

    const vehicleBand = vehicleListFitmentFields.length > 0 && (
        <div onFocus={() => setFiltersOpen(true)}>
            <s-query-container>
                <s-grid gridTemplateColumns={vehicleGridColumns} gap="small-200" alignItems="end">
                    {nonYearFields.map(field => {
                        const key = normalizeFitmentKey(field);
                        return <FitmentFilterCombobox key={key} label={field.label}
                            options={fieldOptions[key] ?? []}
                            value={getFitmentFieldValue(selectedFitmentValues, field)}
                            disabled={isFitmentFieldDisabled(vehicleListFitmentFields, selectedFitmentValues, field, false)}
                            loading={loadingFieldOptions}
                            placeholder={getFitmentFieldEmptyOptionLabel(field, 'select')}
                            onChange={value => {
                                const nextValues = updateFitmentFieldValue(vehicleListFitmentFields, selectedFitmentValues, field, value);
                                skipNextDebouncedLoadRef.current = true;
                                applySelectedFitmentValues(nextValues);
                                loadVehicles(1, queryValue, selectedSource[0] || '', sortSelected[0],
                                    nextValues.year || '', nextValues.make || '', nextValues.model || '',
                                    selectedAssigned[0] || '', normalizeFitmentFieldValues(vehicleListFitmentFields, nextValues));
                            }} />;
                    })}
                    {fieldEnabled.year && (
                        <>
                            <Combobox label="From year" options={yearOptions} value={filterYearFrom || selectedYear}
                                loading={loadingFieldOptions} placeholder="Any" emptyMessage="No matching years."
                                onChange={value => changeYearRange(value, filterYearTo || selectedYear)} />
                            <Combobox label="To year" options={yearOptions} value={filterYearTo || selectedYear}
                                loading={loadingFieldOptions} placeholder="Any" emptyMessage="No matching years."
                                error={rangeError || undefined}
                                onChange={value => changeYearRange(filterYearFrom || selectedYear, value)} />
                        </>
                    )}
                    <s-button variant="tertiary" onClick={clearVehicleFields} disabled={!hasVehicleSelection}>
                        Clear
                    </s-button>
                </s-grid>
            </s-query-container>
        </div>
    );

    // Replaces the search and filter row while vehicles are selected, as in admin.
    const selectionBar = (selectedResources.length > 0 || allResourcesSelected) && (
        <s-stack direction="inline" gap="small-200" alignItems="center">
            <s-checkbox
                accessibilityLabel="Clear selection"
                checked
                indeterminate={!selection.allOnPage && !allResourcesSelected}
                onChange={() => clearSelection()}
            />
            <s-text fontWeight="semibold">
                {allResourcesSelected
                    ? `All ${totalCount.toLocaleString()} matching vehicles selected`
                    : `${selectedResources.length.toLocaleString()} selected`}
            </s-text>
            {selection.allOnPage && hasNext && !allResourcesSelected && (
                <s-button variant="tertiary" onClick={() => selection.setAllMatching(true)}>
                    Select all matching vehicles
                </s-button>
            )}
            <s-button tone="critical" icon="delete" onClick={onBulkDeleteClick}
                disabled={selectedCount < 1 || rangeResultsPending || loadingInitial}>
                Delete
            </s-button>
        </s-stack>
    );

    const countLabel = rangeError ? 'Check the year range to view vehicles.'
        : vehicleStore.loadError ? 'Vehicles could not be loaded.'
            : loadingInitial || rangeResultsPending ? 'Loading vehicles…'
                : `${totalCount.toLocaleString()} vehicle ${totalCount === 1 ? 'record' : 'records'}${yearView === 'ranges' && vehicleStore.groupCount != null ? ` · ${vehicleStore.groupCount.toLocaleString()} ${vehicleStore.groupCount === 1 ? 'group' : 'groups'}` : ''}`;

    const changeYearView = (view: 'ranges' | 'individual') => {
        if (yearView === view) return;
        yearViewRef.current = view;
        setYearView(view);
        reloadVehicles(1);
    };

    const headerLabel = (field: FitmentField) => combinedIdentity && normalizeFitmentKey(field) === 'model'
        ? 'Vehicle' : yearView === 'ranges' && normalizeFitmentKey(field) === 'year' ? 'Years' : field.label;
    const showEmpty = !loadingInitial && !rangeResultsPending && !vehicleStore.loadError && rows.length === 0;
    const pageTitle = vehicleCatalogLabel || ' ';

    return (
        <>
            <s-modal
                ref={editorModalRef}
                id="pf-vehicle-editor-modal"
                heading={editingGroup ? 'Edit all years' : editingId ? (editingIsCustom ? 'Edit vehicle' : 'Edit system vehicle') : 'Add vehicle'}
            >
                {modalOpen && (
                    <s-stack gap="base">
                        {submitError && <s-banner tone="critical">{submitError}</s-banner>}

                        {editingGroup && (
                            <s-paragraph>
                                Changes apply to <s-text fontWeight="semibold">{formatVehicleYears(editingGroup.map(record => record.fieldValues?.year ?? record.field1))}</s-text>. Assigned products stay assigned.
                            </s-paragraph>
                        )}

                        {mergeReview && (
                            <s-banner tone="warning" heading="This will merge vehicles">
                                {formatVehicleYears(mergeReview.mergeYears)} already {mergeReview.mergeYears.length === 1 ? 'exists' : 'exist'} under this name. Merging combines them and moves {mergeReview.mergeAssignments.toLocaleString()} product {mergeReview.mergeAssignments === 1 ? 'assignment' : 'assignments'}. This can't be undone automatically.
                            </s-banner>
                        )}

                        {editingId && !editingIsCustom && (
                            <s-banner tone="info">
                                <s-stack gap="small-200">
                                    <s-paragraph>This is a system vehicle and cannot be edited. Saving will create a new vehicle.</s-paragraph>
                                    <s-checkbox
                                        label="Persist saved fitments"
                                        checked={persistFitments}
                                        onChange={(event) => setPersistFitments(fieldChecked(event))}
                                    />
                                </s-stack>
                            </s-banner>
                        )}

                        {!editingId && lastAdded && (
                            <s-banner ref={lastAddedEvents} tone={lastAddedExists ? 'info' : 'success'} dismissible
                                heading={lastAddedExists ? 'Vehicle already exists' : 'Vehicle added'}>
                                <s-text fontWeight="semibold">{lastAddedLabel}</s-text>
                                <s-button slot="secondary-actions" onClick={onCopyLast}>Duplicate</s-button>
                            </s-banner>
                        )}

                        {visibleFitmentFields.filter(field => !editingGroup || normalizeFitmentKey(field) !== 'year').map(field => {
                            const key = normalizeFitmentKey(field);
                            const onFieldInput = (value: string) => {
                                setFormValues(previous => ({ ...previous, [key]: value }));
                                setErrors(previous => ({ ...previous, [key]: undefined }));
                                setMergeReview(null);
                                if (lastAdded) dismissLastAdded();
                            };
                            const input = field.dataType === 'Date' ? (
                                <s-date-field
                                    key={key}
                                    label={field.label}
                                    required={field.isRequired}
                                    value={formValues[key] || ''}
                                    details={field.helpText || undefined}
                                    error={errors[key]}
                                    onChange={event => onFieldInput(fieldValue(event))}
                                />
                            ) : (
                                <s-text-field
                                    key={key}
                                    label={field.label}
                                    required={field.isRequired}
                                    value={formValues[key] || ''}
                                    maxLength={Math.min(field.maxLength ?? 400, 400)}
                                    autocomplete="off"
                                    placeholder={field.placeholder || undefined}
                                    details={field.helpText || undefined}
                                    error={errors[key]}
                                    onInput={event => onFieldInput(fieldValue(event))}
                                />
                            );
                            return key === 'year' && !editingIsCustom ? (
                                <s-grid key={key} gridTemplateColumns="1fr 1fr" gap="base">
                                    {input}
                                    <s-text-field
                                        label="End year (optional)"
                                        value={yearTo}
                                        autocomplete="off"
                                        error={errors.yearTo}
                                        onInput={event => {
                                            setYearTo(fieldValue(event));
                                            setErrors(previous => ({ ...previous, yearTo: undefined }));
                                            if (lastAdded) dismissLastAdded();
                                        }}
                                    />
                                </s-grid>
                            ) : input;
                        })}
                    </s-stack>
                )}
                <s-button slot="primary-action" variant="primary" loading={submitting} onClick={editingGroup ? onSaveGroup : onSave}>
                    {mergeReview ? 'Merge and save' : 'Save'}
                </s-button>
                <s-button slot="secondary-actions" disabled={submitting} onClick={handleClose}>
                    Cancel
                </s-button>
            </s-modal>

            <s-modal ref={deleteModalRef} id="pf-delete-vehicles-modal" heading="Delete vehicles">
                {deleteOpen && (
                    <s-paragraph>
                        {deleteInfo.hasStandard && !deleteInfo.hasCustom && (
                            <>System vehicle data cannot be deleted.</>
                        )}
                        {deleteInfo.hasStandard && deleteInfo.hasCustom && (
                            <>System vehicle data cannot be deleted. Only user-added vehicles (and their associated fitments) will be deleted. This action cannot be undone.</>
                        )}
                        {!deleteInfo.hasStandard && deleteInfo.hasCustom && (
                            allResourcesSelected
                                ? <>You’re about to delete <s-text fontWeight="semibold">all {totalCount.toLocaleString()}</s-text> vehicles (and their associated fitments) that match the current filters. This cannot be undone.</>
                                : <>You’re about to delete <s-text fontWeight="semibold">{deleteInfo.customCount}</s-text> selected vehicle(s) (and their associated fitments). This cannot be undone.</>
                        )}
                    </s-paragraph>
                )}
                <s-button slot="primary-action" variant="primary" tone="critical" loading={deleting}
                    disabled={!deleteInfo.hasCustom} onClick={confirmDelete}>
                    Delete
                </s-button>
                <s-button slot="secondary-actions" disabled={deleting} onClick={() => setDeleteOpen(false)}>
                    Cancel
                </s-button>
            </s-modal>

            <s-page heading={pageTitle} inlineSize="large">
                <s-button slot="primary-action" variant="primary" onClick={openAdd}>Add vehicle</s-button>
                <s-button slot="secondary-actions" onClick={() => navigate('/storefront?section=selector-fields')}>
                    Manage fields
                </s-button>
                <s-button slot="secondary-actions" onClick={() => navigate('/storefront?section=reference-data')}>
                    Manage data
                </s-button>
                <s-stack gap="base">
                    {/* The s-page heading shows only in the admin title bar; repeat it on the page. */}
                    <s-heading fontSize="large-200">{pageTitle}</s-heading>

                    {vehicleStore.loadError && (
                        <s-banner tone="critical" heading="Couldn’t load vehicles">
                            <s-paragraph>{vehicleStore.loadError}</s-paragraph>
                            <s-button slot="secondary-actions" loading={loadingInitial} onClick={() => reloadVehicles(1)}>
                                Try again
                            </s-button>
                        </s-banner>
                    )}

                    <s-section accessibilityLabel="Vehicles">
                        <s-stack gap="base">
                            {vehicleBand}
                            {selectionBar || (
                                <MappingFilters
                                    hasVehicleContext={false}
                                    mappedOnly={false}
                                    onMappedOnlyChange={() => {}}
                                    sortOptions={sortOptions}
                                    sortSelected={sortSelected}
                                    onSort={setSortSelected}
                                    queryValue={queryValue}
                                    queryPlaceholder="Search vehicles"
                                    onQueryChange={setQueryValue}
                                    onClearAll={clearListFilters}
                                    filters={filters}
                                    appliedFilters={appliedFilters}
                                />
                            )}
                            <s-stack direction="inline" justifyContent="space-between" alignItems="center" gap="small-200">
                                <s-text color="subdued">{countLabel}</s-text>
                                {fieldEnabled.year && (
                                    <s-stack direction="inline" gap="small-100" accessibilityLabel="Year display">
                                        <s-press-button pressed={yearView === 'ranges'} onClick={() => changeYearView('ranges')}>
                                            Year ranges
                                        </s-press-button>
                                        <s-press-button pressed={yearView === 'individual'} onClick={() => changeYearView('individual')}>
                                            Individual years
                                        </s-press-button>
                                    </s-stack>
                                )}
                            </s-stack>
                            <s-table
                                ref={tableEvents}
                                loading={loadingInitial}
                                paginate={hasNext || hasPrevious}
                                hasNextPage={hasNext}
                                hasPreviousPage={hasPrevious}
                            >
                                <s-table-header-row>
                                    {displayedColumns.map((field, index) => index === 0 ? (
                                        <s-table-header key={normalizeFitmentKey(field)} listSlot="primary">
                                            <s-stack direction="inline" gap="base" alignItems="center">
                                                <s-checkbox
                                                    accessibilityLabel="Select all vehicles on this page"
                                                    checked={selection.allOnPage}
                                                    indeterminate={selection.someOnPage}
                                                    disabled={rows.length === 0}
                                                    onChange={(event) => selection.togglePage(fieldChecked(event))}
                                                />
                                                <s-text>{headerLabel(field)}</s-text>
                                            </s-stack>
                                        </s-table-header>
                                    ) : (
                                        <s-table-header key={normalizeFitmentKey(field)} listSlot="labeled">{headerLabel(field)}</s-table-header>
                                    ))}
                                    <s-table-header listSlot="labeled">Assigned products</s-table-header>
                                    <s-table-header listSlot="inline">
                                        <s-text accessibilityVisibility="exclusive">Actions</s-text>
                                    </s-table-header>
                                </s-table-header-row>
                                <s-table-body>
                                    <VehicleCatalogRows vehicles={filteredVehicles} fields={vehicleListFitmentFields}
                                        groups={yearView === 'ranges' ? vehicleStore.yearGroups : null}
                                        expanded={expandedGroups} onToggle={key => setExpandedGroups(previous => {
                                            const next = new Set(previous);
                                            if (next.has(key)) next.delete(key); else next.add(key);
                                            return next;
                                        })}
                                        isSelected={id => allResourcesSelected || selection.isSelected(id)}
                                        onSelect={(ids, checked) => ids.forEach(id => selection.toggle(id, checked))}
                                        onEdit={onEditClick} onEditGroup={onEditGroupClick} onProducts={openAssignedProducts} />
                                </s-table-body>
                            </s-table>
                            {showEmpty && (
                                <s-box padding="base">
                                    {showCreateVehicleEmptyState ? (
                                        <s-empty-state heading="Add your first vehicle">
                                            <s-text slot="subheading">
                                                Add vehicles manually or import them from a file to start your fitment catalog.
                                            </s-text>
                                            <s-button slot="primary-action" variant="primary" onClick={openAdd}>Add vehicle</s-button>
                                            <s-button slot="secondary-actions" onClick={() => navigate('/imports')}>Import vehicles</s-button>
                                        </s-empty-state>
                                    ) : (
                                        <s-empty-state heading="No vehicles match your filters">
                                            <s-text slot="subheading">Try a different search or remove some filters.</s-text>
                                            <s-button slot="primary-action" onClick={onClearAll}>
                                                Clear filters
                                            </s-button>
                                        </s-empty-state>
                                    )}
                                </s-box>
                            )}
                        </s-stack>
                    </s-section>
                </s-stack>
            </s-page>
        </>
    );
};

export default observer(VehiclesPage);
